import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box } from '@mui/material';
import FormView from '../../../../components/views/formView';
import { useSurveyForm } from './hook';
import StepperGeneral from '../../../../components/general/stepper/stepper';
import { SurveyInformation } from './steps/surveyInformation';
import { SurveyQuestions } from './steps/surveyQuestions';
import LoadingButtonGeneral from '../../../../components/general/buttons/loadingButtonGeneral';
import ActionButtonGroup from '../../../../components/general/buttons/actionButtonGroup';
import { typesRedux } from '../../../../../shared/types/typesRedux';
import { SURVEYS, SURVEYS_NEW } from '../../../../../routes/paths';
import { RootState } from '../../../../../redux/store';
import { SurveyLogs } from '../logs';
import { asyncGetSurveyLogs } from '../../../../../services/survey.service';

export const SurveyForm = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { survey, reloadSurvey } = useSurveyForm();
    const { activeStep } = useSelector((state: RootState) => state.general);
    const [saveInfo, setSaveInfo] = useState<(() => void) | undefined>();
    const [saveQuestions, setSaveQuestions] = useState<
        (() => void) | undefined
    >();
    const [submit, setSubmit] = useState<boolean>(false);
    const [onSave, setOnSave] = useState<boolean>(false);
    const [showHistorical, setShowHistorical] = useState(false);
    const [historical, setHistorical] = useState<any[]>([]);

    useEffect(() => {
        // Reset stepper when component unmounts
        return () => {
            dispatch({ type: typesRedux.resetStepper });
        };
    }, []);

    useEffect(() => {
        if (survey?.id && showHistorical) {
            loadHistorical();
        }
    }, [survey?.id, showHistorical]);

    const loadHistorical = async () => {
        if (survey?.id) {
            const data = await asyncGetSurveyLogs(survey.id);
            setHistorical(data);
        }
    };

    const toggleHistorical = () => {
        setShowHistorical(!showHistorical);
    };

    const steps = [
        {
            label: 'Datos de la Encuesta',
            component: (
                <SurveyInformation
                    surveyInfo={survey}
                    idSurvey={survey?.id}
                    onSubmitSave={(value: boolean) => setSubmit(value)}
                    saveInfo={setSaveInfo}
                    onSave={(value: boolean) => setOnSave(value)}
                    onSaveSuccess={reloadSurvey}
                />
            ),
        },
        {
            label: 'Preguntas y Respuestas',
            component: (
                <SurveyQuestions
                    idSurvey={survey?.id}
                    saveQuestions={setSaveQuestions}
                />
            ),
        },
    ];

    const handleNext = () => {
        if (activeStep === 0) {
            saveInfo && saveInfo();
        } else {
            saveQuestions && saveQuestions();
        }
    };

    const handleBack = () => {
        if (activeStep === 0) {
            navigate(SURVEYS);
        } else {
            dispatch({ type: typesRedux.backStepper });
        }
    };

    // Si está mostrando logs, solo mostrar los logs
    if (showHistorical && survey?.id) {
        return (
            <>
                <FormView
                    title="Historial de Cambios"
                    additionalButtons={
                        <ActionButtonGroup
                            id={survey?.id || ''}
                            buttonCreate={{
                                show: Boolean(survey?.id),
                                route: SURVEYS_NEW,
                            }}
                            buttonSearch={{
                                show: true,
                                route: SURVEYS,
                            }}
                            buttonLog={{
                                show: Boolean(survey?.id),
                            }}
                            onHistoricalShow={toggleHistorical}
                        />
                    }
                />
                <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                    <SurveyLogs historical={historical} showHistorical={true} />
                </Box>
            </>
        );
    }

    return (
        <>
            <FormView
                title={survey?.id ? 'Editar encuesta' : 'Crear encuesta'}
                additionalButtons={
                    <ActionButtonGroup
                        id={survey?.id || ''}
                        buttonSearch={{
                            show: true,
                            route: SURVEYS,
                        }}
                    />
                }
            />
            <StepperGeneral steps={steps} />
            <Grid item xs={12} mt={5} mb={10}>
                <LoadingButtonGeneral
                    disabled={submit || onSave}
                    loading={submit || onSave}
                    title={activeStep === 0 ? 'Siguiente' : 'Agregar Pregunta'}
                    sx={{ mr: 1 }}
                    onClick={handleNext}
                />
                <LoadingButtonGeneral
                    disabled={submit || onSave}
                    title="Volver"
                    variant="outlined"
                    onClick={handleBack}
                />
            </Grid>
        </>
    );
};
